import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'reactDOM';
import _ from 'lodash';

import {
  hoc,
  animation,
  promiseUtils,
  type CancelablePromise,
} from '#packages/util';
import * as coreBi from '#packages/coreBi';
import * as util from '#packages/util';
import constants from '#packages/constants';

import connect from './mobileFrameConnect';
import { MobileFrameComponent } from './mobileFrameComponent';

import type { SendBiFunction } from 'types/bi';
import type { MobileFramePosition } from 'types/core';
import type { PreviewType } from '#packages/stateManagement';

const { makeCancelablePromise, isCancelablePromiseCancelError } = promiseUtils;

export interface MobileFrameOwnProps {
  frameless: boolean;
  shouldRenderFixedStageTopGap: boolean;
  isMobileWizardPreviewFrameVisible: boolean;
}

export interface MobileFrameStateProps {
  siteScale: number;
  previewMode: boolean;
  siteX: number;
  shouldHideShadow: boolean;
  isPreviewMode: boolean;
  previewType: PreviewType;
  mobileWizardOpeningState: keyof typeof constants.MOBILE.MOBILE_WIZARD.OPENING_STATE;
}

export interface MobileFrameDispatchProps {
  refreshMobileFramePosition: (rect: MobileFramePosition) => void;
  sendBI: SendBiFunction;
}

type MobileFrameProps = MobileFrameOwnProps &
  MobileFrameDispatchProps &
  MobileFrameStateProps;

class MobileFrameComp extends React.Component<MobileFrameProps> {
  static displayName = 'mobileFrame';

  // propTypes used in renderWhenMutated HOC
  static propTypes = {
    siteScale: PropTypes.number.isRequired,
    previewMode: PropTypes.bool.isRequired,
    siteX: PropTypes.number.isRequired,
    refreshMobileFramePosition: PropTypes.func.isRequired,
    sendBI: PropTypes.func,
  };

  private waitingForUpdatePosition:
    | CancelablePromise<MobileFramePosition>
    | undefined;

  frameRef: React.RefObject<HTMLDivElement> = React.createRef();

  componentDidMount() {
    this.updateMobileFramePosition();
  }

  componentDidUpdate() {
    this.updateMobileFramePosition();
  }

  componentWillUnmount() {
    this.waitingForUpdatePosition?.cancel();
  }

  mobileDeviceBtnClick = (event: AnyFixMe) => {
    const { clientX } = event;
    const centerX = window.innerWidth / 2;
    const distanceFromCenter = Math.abs(clientX - centerX);

    if (distanceFromCenter < 25) {
      const frameBottomDOMNode: HTMLElement = _.head(
        ReactDOM.findDOMNode(this).querySelectorAll(
          '[data-aid="mobile-frame-bottom"]',
        ),
      );
      const boundingClientRect = frameBottomDOMNode.getBoundingClientRect();

      const yLocationRelativeToBottomFrameImg =
        event.clientY - boundingClientRect.top;

      if (
        yLocationRelativeToBottomFrameImg > 14 &&
        yLocationRelativeToBottomFrameImg < 58
      ) {
        this.props.sendBI(
          coreBi.events.mobileEditor.MOBILE_EDITOR_DEVICE_CLICKED,
        );
      }
    }
  };

  async updateMobileFramePosition() {
    this.waitingForUpdatePosition?.cancel();
    this.waitingForUpdatePosition = makeCancelablePromise(
      animation.waitForAnimationEnd<MobileFramePosition>(
        this.getFrameClientPosition,
      ),
    );

    try {
      const mobileFramePosition = await this.waitingForUpdatePosition.promise;

      this.props.refreshMobileFramePosition(mobileFramePosition);
    } catch (error) {
      if (!isCancelablePromiseCancelError(error)) {
        throw error;
      }
    }
  }

  getFrameClientPosition = () => {
    return _.pick(
      ReactDOM.findDOMNode(this.frameRef.current)?.getBoundingClientRect(),
      ['top', 'left', 'height', 'width'],
    );
  };

  getFrameStyle = () => {
    const style: React.CSSProperties = {};

    if (!this.props.previewMode && this.props.siteScale !== 1) {
      style.height = `${Math.round(100 / this.props.siteScale)}%`;
      style.transform = `scale(${this.props.siteScale})`;
    }

    return style;
  };

  shouldHideMobileFrame = () =>
    !this.props.isPreviewMode &&
    this.props.mobileWizardOpeningState !==
      constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.IN_PROGRESS;

  shouldRenderFixedStageTopGap = () =>
    util.fixedStage.shouldRenderFixedTopGap(
      this.props.isPreviewMode,
      this.props.previewType,
    );

  shouldMobileWizardPreviewFrameBeVisible = () =>
    this.props.mobileWizardOpeningState ===
      constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.IN_PROGRESS ||
    this.props.mobileWizardOpeningState ===
      constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.FINISH;

  render() {
    return (
      <MobileFrameComponent
        ref={this.frameRef}
        frameStyle={this.getFrameStyle()}
        noShadow={this.props.shouldHideShadow}
        frameless={this.shouldHideMobileFrame()}
        onMobileDeviceBtnClick={this.mobileDeviceBtnClick}
        previewMode={this.props.previewMode}
        shouldRenderFixedStageTopGap={this.shouldRenderFixedStageTopGap()}
        isMobileWizardPreviewFrameVisible={this.shouldMobileWizardPreviewFrameBeVisible()}
      />
    );
  }
}

const MobileFrame = _.flow(hoc.renderWhenMutated, connect)(MobileFrameComp);

export default MobileFrame;

MobileFrame.pure = MobileFrameComp;
